import { memoize } from 'lodash';
import {
  DocumentServicesObject,
  CompRef,
  AppDataComponent,
} from '@wix/document-services-types';
import { SlotDefinition } from '@wix/editor-platform-host-integration-apis';
import {
  BLOCKS_WIDGET,
  BUILDER_WIDGET,
  OOI_PAGE,
  OOI_WIDGET,
} from '../constants';
import { IExperimentsApiAdapter } from '../../../utils/experimentsApiAdapter';
import { EditorElement } from '@wix/component-protocol/schema';
import { ManifestSlotPathFinder } from './manifestSlotPathFinder';
import { getAppData } from './getAppData';

interface WidgetSlotReferenceData {
  slotId: string;
  dataItemKey?: string; // only for builder slots
  innerElementPath?: string[]; // only for builder slots
}

const slotIdReaders: {
  [key: string]: (component: AppDataComponent) => WidgetSlotReferenceData[];
} = {
  [BUILDER_WIDGET]: builderSlotIdReader,
  [OOI_WIDGET]: ooiSlotIdReader,
  [OOI_PAGE]: ooiSlotIdReader,
  [BLOCKS_WIDGET]: blocksSlotIdReader,
} as const;

function builderSlotIdReader(
  component: AppDataComponent,
): WidgetSlotReferenceData[] {
  // @ts-expect-error
  const manifest: EditorElement = component.data?.editorElement;
  if (!manifest) {
    return [];
  }
  const finder = new ManifestSlotPathFinder(manifest);
  return finder.findAllSlotsInManifest().map((slot) => ({
    slotId: slot.slotId,
    dataItemKey: slot.slotKeyName,
    innerElementPath: slot.innerElementPath,
  }));
}

function ooiSlotIdReader(
  component: AppDataComponent,
): WidgetSlotReferenceData[] {
  const slotIds: WidgetSlotReferenceData[] =
    // @ts-expect-error
    component?.data?.outOfIframeData?.slotIds?.map((slotId) => ({ slotId })) ??
    [];
  return slotIds;
}

function blocksSlotIdReader(
  component: AppDataComponent,
): WidgetSlotReferenceData[] {
  // @ts-expect-error
  return component?.data?.slotIds?.map((slotId) => ({ slotId })) ?? [];
}

function readSlotIds(
  component: AppDataComponent | undefined,
): WidgetSlotReferenceData[] {
  if (
    !component?.type ||
    typeof slotIdReaders[component?.type] !== 'function'
  ) {
    return [];
  }
  return slotIdReaders[component?.type](component);
}

function getSlotsFromCSM(
  documentServicesAPI: DocumentServicesObject,
  widgetCompRef: CompRef,
): SlotDefinition[] {
  const { appDefinitionId, widgetId } = getAppData(
    documentServicesAPI,
    widgetCompRef,
  );
  const data = documentServicesAPI.tpa.app.getDataByAppDefId(appDefinitionId);
  const component = data?.components?.find((c) => c.componentId === widgetId);
  const slotIds: WidgetSlotReferenceData[] = readSlotIds(component);
  return slotIds.map(({ slotId, dataItemKey, innerElementPath }) => {
    const slot = data?.components?.find((c) => c.componentId === slotId);
    // @ts-expect-error
    const name = dataItemKey ?? slot?.data?.slotKeyName!;
    // @ts-expect-error
    const slotData = slot?.data?.slotData;
    const blocksSlotRefId = slotData?.widgetOptions?.blocksData?.compRefId;
    const isPublic = slotData?.exposureAndMaturity?.exposure === 'PUBLIC';
    const displayName = slotData?.displayName! || name;
    const slotDefinition: SlotDefinition = {
      appDefinitionId,
      widgetId,
      name,
      displayName,
      isPublic,
      id: slotId,
    };
    if (blocksSlotRefId) {
      slotDefinition.blocksSlotRefId = blocksSlotRefId;
    }
    if (innerElementPath) {
      slotDefinition.innerElementPath = innerElementPath;
    }
    return slotDefinition;
  });
}

// NOTE: It's happens often, but widget slots are not changed during the session, so we can cache the result
const getCachedSlotDefinitions = memoize(
  getSlotsFromCSM,
  (documentServicesAPI, compRef) =>
    compRef &&
    `${compRef.id}-${compRef.type}-${documentServicesAPI.components.is.rendered(
      compRef,
    )}`,
);

export const getSlotDefinitions = (
  documentServicesAPI: DocumentServicesObject,
  widgetCompRef: CompRef,
  experimentsAPI: IExperimentsApiAdapter,
): SlotDefinition[] => {
  if (!experimentsAPI.enabled('se_pluggable_slots')) {
    return [];
  }
  return getCachedSlotDefinitions(documentServicesAPI, widgetCompRef);
};

export const filterPublicSlots = (
  allSlots: SlotDefinition[],
): SlotDefinition[] => {
  return allSlots.filter((slot) => slot.isPublic);
};
